// =============================================================================
// AccountDataSettings — "Your data" (Settings → Account, last section)
// =============================================================================
// Download my data (GET /api/auth/export) and Delete account. Deletion is a
// tombstone on the server (services/accountDeletion.ts): the account and
// profile are erased, messages stay as "Deleted user". It's blocked while you
// own servers, so the dialog lists them first; it needs the password, plus a
// 2FA code when 2FA is on.
// =============================================================================

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, clearToken } from '../api';
import { useApp } from '../store';
import { wsClient } from '../ws';
import { Button, Input, Modal, Spinner } from './ui';
import { toast } from './Toast';

export function AccountDataSettings() {
  const [exporting, setExporting] = useState(false);
  const [open, setOpen] = useState(false);
  const closeDialog = useCallback(() => setOpen(false), []);

  async function download() {
    setExporting(true);
    try {
      const { blob, filename } = await api.exportAccountData();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      toast.error(err, 'Could not export your data');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="border-t border-border-soft pt-3 mt-4">
      <div className="text-sm text-text font-medium mb-1">Your data</div>
      <div className="text-xs text-text-muted mb-3">
        Download a copy of your account, servers, friends, and every message you've written.
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={() => void download()} disabled={exporting}>
          {exporting ? <Spinner size={14} /> : 'Download my data'}
        </Button>
        <Button type="button" variant="danger" onClick={() => setOpen(true)}>
          Delete account…
        </Button>
      </div>
      {open && <DeleteAccountDialog onClose={closeDialog} />}
    </div>
  );
}

function DeleteAccountDialog({ onClose }: { onClose: () => void }) {
  const setUser = useApp((s) => s.setUser);
  const [owned, setOwned] = useState<{ id: string; name: string }[] | null>(null);
  const [needsCode, setNeedsCode] = useState(false);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void Promise.all([api.deleteAccountBlockers(), api.twoFactorStatus()])
      .then(([b, tf]) => {
        if (!alive) return;
        setOwned(b.ownedServers);
        setNeedsCode(tf.enabled);
      })
      .catch((err) => {
        toast.error(err, 'Could not check your account');
        onClose();
      });
    return () => {
      alive = false;
    };
  }, [onClose]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.deleteAccount({ password, ...(needsCode ? { code } : {}) });
      clearToken();
      wsClient.disconnect();
      setUser(null);
      toast.info('Your account has been deleted.');
    } catch (err) {
      if (err instanceof ApiError) {
        const d = err.details as { ownedServers?: { id: string; name: string }[]; needsCode?: boolean } | undefined;
        if (d?.ownedServers) setOwned(d.ownedServers);
        if (d?.needsCode) setNeedsCode(true);
        setError(err.message);
      } else {
        setError('Could not delete your account');
      }
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} title="Delete your account">
      {owned === null ? (
        <div className="py-6 flex justify-center"><Spinner size={16} /></div>
      ) : owned.length > 0 ? (
        <div className="space-y-3 text-sm text-text">
          <p>You own {owned.length === 1 ? 'a server' : `${owned.length} servers`}. Before you can delete your account,
            transfer ownership (click a member in the member list → Transfer ownership) or delete {owned.length === 1 ? 'it' : 'them'}:</p>
          <ul className="list-disc pl-5 text-text-muted">
            {owned.map((s) => <li key={s.id} className="wrap-anywhere">{s.name}</li>)}
          </ul>
          <div className="flex justify-end">
            <Button type="button" onClick={onClose}>OK</Button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <div className="text-sm text-text space-y-2">
            <p><strong>This can't be undone.</strong> Your profile, friends, settings, and sign-ins are erased and you
              leave every server.</p>
            <p className="text-text-muted">Messages you sent stay in their conversations, shown as from
              "Deleted user". Want a copy first? Use <em>Download my data</em>.</p>
          </div>
          <Input
            label="Password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoFocus
          />
          {needsCode && (
            <Input
              label="Two-factor code (or a recovery code)"
              name="code"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={16}
              required
            />
          )}
          {error && <div className="text-xs text-danger" role="alert">{error}</div>}
          <div className="flex flex-wrap justify-end gap-2 pt-1">
            {/* Cancel first + no autofocus on the destructive button, so a
                reflexive Enter in the password field is the only path and it
                still needs the password typed. */}
            <Button type="button" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button type="submit" variant="danger" disabled={busy || !password}>
              {busy ? <Spinner size={14} /> : 'Delete my account'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
